import React, { useEffect, useRef, useState } from 'react';
import type { WorkspacePerson } from '../../utils/ideas';

/** The time now, renewed every minute: "5 min ago" stays true while the page is open. */
export const useNow = (everyMs: number = 60_000): number => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), everyMs);
        return () => clearInterval(timer);
    }, [everyMs]);
    return now;
};

const initialsOf = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    return ((words[0]?.[0] ?? '?') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
};

/** A person's round picture, or their initials. */
export const Avatar: React.FC<{ member?: Pick<WorkspacePerson, 'name' | 'picture'>; name?: string; size?: 20 | 24 | 32 | 40 }> = ({ member, name, size = 24 }) => {
    const shown = member?.name || name || 'Someone';
    const sizeClass = { 20: '[--size:20px]', 24: '[--size:24px]', 32: '[--size:32px]', 40: '[--size:40px]' }[size];
    return (
        <span className={`ns-avatar ${sizeClass}`} title={shown}>
            {member?.picture ? <img src={member.picture} alt="" referrerPolicy="no-referrer" /> : initialsOf(shown)}
        </span>
    );
};

export const errorText = (e: unknown, fallback: string = 'Something went wrong. Try again.'): string =>
    (e instanceof Error && e.message ? e.message : fallback);

/** Closes a menu when the pointer goes down outside it, or Escape is pressed */
export const useCloseOnOutside = <T extends HTMLElement>(open: boolean, onClose: () => void): React.RefObject<T> => {
    const ref = useRef<T>(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('keydown', onKey, true);
        return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); };
    }, [open, onClose]);
    return ref;
};

interface BoardCardProps {
    id: string;
    /** The tone of the head: a kind's colour, or a person's */
    tone: string;
    /** The round mark at the left of the head: the kind's icon in its chip, or the person's picture */
    mark: React.ReactNode;
    /** The title, or the field that renames it */
    title: React.ReactNode;
    /** The buttons at the right of the head */
    tools?: React.ReactNode;
    /** Ringed: what a link asked for */
    highlighted?: boolean;
    className?: string;
    /** More data attributes on the card */
    data?: Record<string, string | undefined>;
    children: React.ReactNode;
}

/**
 * The frame every card of the board has: the node card's own, a white card with a 40px head in
 * the flat tint of its tone, a round mark and the title in the tone's deep colour, and the tools
 * at the right. The head is what a card is dragged by.
 */
export const BoardCard: React.FC<BoardCardProps> = ({ id, tone, mark, title, tools, highlighted = false, className = '', data = {}, children }) => (
    <article
        className={`canvas-node relative flex flex-col rounded-2xl border bg-white ${className}`}
        data-tone={tone}
        data-idea-card={id}
        data-selected={highlighted ? 'true' : undefined}
        tabIndex={-1}
        {...data}
    >
        <div className="canvas-node-head cursor-grab" data-card-head>
            <div className="flex items-center gap-2 min-w-0 flex-1">
                {mark}
                {title}
            </div>
            {tools && <div className="flex items-center gap-0.5 shrink-0" data-card-tools>{tools}</div>}
        </div>
        <div className="flex flex-col rounded-b-2xl">{children}</div>
    </article>
);

const SCENE_HEADING = /^(INT\.|EXT\.|INT\/EXT\.?|I\/E\.|EST\.)/i;
const CHARACTER = /^[A-Z][A-Z0-9 .'\-]{1,28}(\s*\([A-Z.' ]+\))?$/;

/**
 * Writing shown as writing: a short first line as its name, scene headings and a character's
 * name in capitals set apart as in a script, paragraphs kept.
 */
export const ScriptText: React.FC<{ text: string; className?: string; skipFirstLine?: boolean }> = ({ text, className = '', skipFirstLine = false }) => {
    const all = text.split('\n');
    const first = all.findIndex(line => line.trim());
    // The first line left out (it is said in the card's head), and the blank lines after it
    const lines = skipFirstLine && first >= 0 && all.slice(first + 1).some(line => line.trim()) ? all.slice(first + 1).join('\n').replace(/^\n+/, '').split('\n') : all;
    const named = lines === all ? first : -1;
    return (
        <div className={`font-serif text-[15px] leading-7 text-ink [overflow-wrap:anywhere] ${className}`} data-script-text>
            {lines.map((line, i) => {
                const words = line.trim();
                if (!words) return <div key={i} className="h-3" aria-hidden="true" />;
                if (i === named && words.length <= 80 && lines.length > 1) return <p key={i} className="font-sans text-base font-bold leading-6 mb-1.5 text-ink">{words}</p>;
                if (SCENE_HEADING.test(words)) return <p key={i} className="font-sans text-xs font-bold tracking-wide uppercase mt-2 text-ink">{words}</p>;
                if (CHARACTER.test(words) && lines[i + 1]?.trim() && !SCENE_HEADING.test(lines[i + 1])) return <p key={i} className="font-sans text-xs font-semibold tracking-wide mt-2 text-ink-soft">{words}</p>;
                return <p key={i}>{line}</p>;
            })}
        </div>
    );
};
